import { useContext, useEffect, useMemo } from "react";

import {
  DocViewContext,
  type DocViewContextType,
  type PageTabVariant,
} from "../providers/DocViewProvider";
import {
  GLOBAL_SHARED_ACCESS_ID,
  INSTRUCTOR_SHARED_ACCESS_ID,
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../shared/access-id";
import type { WorksheetType } from "../shared/types/worksheet";
import { createId } from "../shared/utils";

import { logInfoWithDebugFlag, rollbarAndLogWarning } from "./logger";

import { SUBMISSION_ID_QUERY_PARAM, WAITED_TOO_LONG_TIMEOUT_MS } from "components/constants";
import { useResponsesForPage } from "components/materials/page/responses";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { InteractionMode, PageType, UserType } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCollaborationPermissionsForDoc,
  selectCurrentSection,
  selectCurrentUserId,
  selectIsEditModeEnabled,
  selectIsStudentPreview,
  selectIsTimeline,
  selectPageAccessDataForAccessId,
  selectPageGroupForReadyUser,
  selectReadiedUsersInPageGroup,
  userSelectors,
} from "store/selectors";
import { authStateActions } from "store/slices/auth";
import { useListSelector } from "store/store-hooks";
import { isGroupInteractionMode, isSharedClassPage, pageIsDisabled } from "utils/page-utils";
import { useQueryParamsManager } from "utils/routing";

export const blankInstructorNotesWorksheetData = () =>
  ({
    b: [
      {
        t: "Instructions" as const,
        id: createId(),
        f: [
          {
            t: "Text" as const,
            id: createId(),
            v: {},
          },
        ],
      },
    ],
    d: {},
  }) as WorksheetType;

// This indicates the group/user/mode that the user is trying to read/write from.
const calculateAccessId = (
  page: PageType,
  canAuthor: boolean,
  canParticipate: boolean,
  userId: string | null | undefined,
  pageGroupId?: string,
) => {
  if (!userId) {
    return null;
  }

  const interactionMode = page?.interaction_mode;
  if (interactionMode === InteractionMode.SHARED_CLASS) {
    return GLOBAL_SHARED_ACCESS_ID;
  }

  if (canAuthor) {
    return INSTRUCTOR_SHARED_ACCESS_ID;
  }

  if (isGroupInteractionMode(interactionMode)) {
    return pageGroupId ? groupAccessIdFromGroupId(pageGroupId) : null;
  }

  if (!canParticipate) {
    return GLOBAL_SHARED_ACCESS_ID;
  }

  return studentAccessIdFromUserId(userId);
};

/** Decode an access id and return the corresponding list of User objects, or null if there is an error. */
export const useUsersFromAccessId = (accessId: string, page: PageType): UserType[] => {
  const userId = isStudentAccessId(accessId) ? getStudentIdFromAccessId(accessId) : null;
  const pageGroupId = isGroupAccessId(accessId) ? getGroupIdFromAccessId(accessId) : null;
  const users = useAppSelector(userSelectors.selectEntities);
  const usersFromPageGroup = useListSelector((s) => selectReadiedUsersInPageGroup(s, pageGroupId));
  const { isFeaturing } = useContext(DocViewContext);

  // TODO: consider optimizing by caching this
  const { responses, anonymize } = useResponsesForPage(page, isFeaturing);

  if (userId) {
    if (anonymize) {
      const response = responses.find((r) => r.id === accessId);
      if (response) {
        return [
          {
            id: userId,
            first_name: response.title,
            last_name: "",
            email: "",
            is_superuser: false,
            is_anonymized: true,
          },
        ];
      }
    } else {
      // If the indicated User can be found in the current SectionUser data, return that User.
      const user = users[userId];
      if (user) {
        return [user];
      }
    }

    // If the current Page was imported from another environment, compensate for missing
    // SectionUsers by returning a fake User object.
    if (page?.is_imported) {
      return [{ id: userId, first_name: "", last_name: "", email: "", is_superuser: false }];
    }
  }

  if (pageGroupId) {
    if (anonymize) {
      const response = responses.find((r) => r.id === accessId);
      if (response) {
        return [
          {
            id: pageGroupId,
            first_name: response.title,
            last_name: "",
            email: "",
            is_superuser: false,
            is_anonymized: true,
          },
        ];
      }

      // If a set of Users can be determined from the current set of PageGroupUsers, return those Users.
    } else if (usersFromPageGroup.length > 0) {
      return usersFromPageGroup;
    }

    // If the current page was imported from another environment, compensate for missing
    // PageGroupUsers by returning a fake User object.
    if (page?.is_imported) {
      return [
        {
          id: pageGroupId,
          first_name: "Group",
          last_name: pageGroupId,
          email: "",
          is_superuser: false,
        },
      ];
    }
  }

  // TODO: maybe raise an error in this case instead of returning []?
  return [];
};

export const useCanLoadDoc = (docId: string) => {
  const userId = useAppSelector(selectCurrentUserId);
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const { canAuthorSection, canSeeAllDocs } = useCapabilities();
  const permissionsForThisPage = useAppSelector((s) =>
    selectCollaborationPermissionsForDoc(s, docId),
  );
  const dispatch = useAppDispatch();

  const canLoadDoc = Boolean(
    canSeeAllDocs || canAuthorSection || isStudentPreview || permissionsForThisPage,
  );

  // We want to track how often users run into infinite (or unreasonably long) loading states
  useEffect(() => {
    if (!canLoadDoc) {
      const timeout = setTimeout(() => {
        rollbarAndLogWarning("canLoadDoc never resolved to true", {
          docId,
          userId,
          canAuthorSection,
          hasPermissions: Boolean(permissionsForThisPage),
        });
      }, WAITED_TOO_LONG_TIMEOUT_MS);

      dispatch(authStateActions.setShouldRequestAuth(true));

      logInfoWithDebugFlag("debug_pubsub", "Requesting auth due to missing load permissions", {
        docId,
      });

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadDoc]);

  if (!docId) {
    return false;
  }

  return canLoadDoc;
};

export const useCanWriteDoc = (
  docId: string,
  accessId: string | null | undefined,
  selectedPageTabVariant?: PageTabVariant | null,
) => {
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const { canAuthorSection, canSeeAllDocs } = useCapabilities();
  const permissionsForThisPage = useAppSelector((s) =>
    selectCollaborationPermissionsForDoc(s, docId),
  );
  const dispatch = useAppDispatch();

  // We need both canAuthorOrg and canAuthorSection in here, as org admins that are course students
  // do still always have view permission to all pages, but canAuthorSection will be false for them.
  const canAlwaysSeeDoc =
    canSeeAllDocs || canAuthorSection || isStudentPreview || permissionsForThisPage?.rollup;

  const isGroupAndCanSeeGroup =
    isGroupAccessId(accessId) &&
    permissionsForThisPage?.group_ids.includes(getGroupIdFromAccessId(accessId));

  const isViewingResponsesTab = selectedPageTabVariant === "responses";

  // Note that this doesn't cover the specific case of e.g. trying to show a different student's
  // access ID on an individual page, without the rollup permissions. We don't currently have that
  // case, but if we did, we'd need to add a check here that the user has the specific access ID
  // they're trying to look up.
  const isOtherAndCanSeePage =
    !isGroupAccessId(accessId) && !isViewingResponsesTab && permissionsForThisPage;

  const canWriteDoc = Boolean(canAlwaysSeeDoc || isGroupAndCanSeeGroup || isOtherAndCanSeePage);

  // We want to track how often users run into infinite (or unreasonably long) loading states
  useEffect(() => {
    if (!canWriteDoc) {
      const timeout = setTimeout(() => {
        rollbarAndLogWarning("canWriteDoc never resolved to true", {
          docId,
          accessId,
          selectedPageTabVariant,
          isStudentPreview,
          canSeeAllDocs,
          canAuthorSection,
          permissions: JSON.stringify(permissionsForThisPage),
        });
      }, WAITED_TOO_LONG_TIMEOUT_MS);

      dispatch(authStateActions.setShouldRequestAuth(true));
      logInfoWithDebugFlag("debug_pubsub", "Requesting auth due to missing write permissions", {
        docId,
        selectedPageTabVariant,
        accessId,
      });

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWriteDoc]);

  return canWriteDoc;
};

export const useDocContext = (
  page: PageType,
  accessIdOverride?: string | null,
  selectedPageTabVariant?: PageTabVariant | null,
  isProjecting?: boolean,
  isFeaturing?: boolean,
  isStaticContent?: boolean,
): DocViewContextType => {
  const { getQueryParam } = useQueryParamsManager();
  const docId = page?.worksheet_id;
  const pageLocked = Boolean(page?.locked_at);
  const pageReleased = Boolean(page?.released_at);

  const { canAuthorSection, canEditStudentDraft, canParticipateInSection } = useCapabilities();
  const currentSection = useAppSelector(selectCurrentSection);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const pageGroup = useAppSelector((s) => selectPageGroupForReadyUser(s, page?.id));
  const isAssessing = useIsAssessing();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isTimeline = useAppSelector(selectIsTimeline);
  const interactionMode = page?.interaction_mode;
  const lockedForStudent = pageLocked && canParticipateInSection;
  const isViewingResponsesTab = selectedPageTabVariant === "responses";
  const isViewingSeededContentTab = selectedPageTabVariant === "seeded_content";
  const isViewingMySubmissionsTab = selectedPageTabVariant === "my_submission";
  const isViewingStudentSubmissionTab = selectedPageTabVariant === "student_submission";

  // The accessIdOverride may be an empty string (not referencing a pageGroup or user) when we're
  // showing Class Responses on rollup view.  Note: we are also telling the page to show
  // seeded content when featuring to students or they are viewing the template path by not
  // providing an accessId.
  const accessId =
    isViewingResponsesTab || isAssessing
      ? accessIdOverride
      : (isViewingSeededContentTab || isFeaturing) &&
          canParticipateInSection &&
          !isSharedClassPage(page)
        ? undefined
        : // Note that for group pages, until the groups are loaded, there's a brief moment where
          // the pageGroup doesn't exist, and the accessId is null.
          calculateAccessId(
            page,
            canAuthorSection,
            canParticipateInSection,
            currentUserId,
            pageGroup?.id,
          );

  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForAccessId(s, page.id, accessId),
  );
  const isDone = canParticipateInSection && Boolean(pageAccessData?.completed_at);

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  // In the UI, draft is represented by `submission=draft` (not by absence of the param).
  // Treat non-real values as "not viewing a submission".
  const isViewingSubmission =
    Boolean(submissionId) && submissionId !== "draft" && submissionId !== "null";

  // Instructors/course-admins can edit a student's draft from the Class Responses tab,
  // but only in Edit Mode, and never when looking at an actual submission.
  const canEditStudentDraftInResponsesTab =
    isViewingResponsesTab &&
    !!accessId &&
    (isStudentAccessId(accessId) || isGroupAccessId(accessId)) &&
    canEditStudentDraft &&
    isEditModeEnabled &&
    !isViewingSubmission;

  const isViewingReadonlyResponses = isViewingResponsesTab && !canEditStudentDraftInResponsesTab;

  const readOnly =
    !accessId ||
    isTimeline ||
    isViewingReadonlyResponses ||
    lockedForStudent ||
    isAssessing ||
    isViewingSeededContentTab ||
    isViewingMySubmissionsTab ||
    isProjecting ||
    // Students can't edit featured blocks (but instructors can)
    (isFeaturing && !canAuthorSection) ||
    pageIsDisabled(page) ||
    isDone;

  const canAuthorPage = !isAssessing && !isViewingResponsesTab && !isProjecting && canAuthorSection;
  // We want students to be able to interact with pages that aren't read-only, and instructors can do the
  // same on shared class pages. We make an exception for Projector Views (that are always read-only) to
  // also set this value to ensure the projector displays the collaborated data.
  const canInteractAsStudent =
    (!readOnly || isProjecting || isTimeline) &&
    (interactionMode === InteractionMode.SHARED_CLASS ||
      canParticipateInSection ||
      canEditStudentDraftInResponsesTab);
  const isAuthoringPage = canAuthorPage && !readOnly && isEditModeEnabled;
  const pageId = page.id;

  const canWriteDoc = useCanWriteDoc(docId, accessId, selectedPageTabVariant);

  const showResponses = isViewingReadonlyResponses ? (accessId ? "ONE" : "ALL") : false;
  const hideStaticBlocks =
    showResponses === "ALL" ||
    (currentSection?.hide_static_blocks_on_student_response_page &&
      (showResponses === "ONE" || isViewingStudentSubmissionTab));

  const context = useMemo(
    () =>
      ({
        readOnly,
        accessId,
        isFeaturing,
        isStaticContent,
        pageId,
        canAuthorPage,
        canInteractAsStudent,
        isAuthoringPage,
        isProjecting,
        canWriteDoc,
        isReleased: pageReleased,
        selectedPageTabVariant: selectedPageTabVariant ?? null,
        showResponses: showResponses as DocViewContextType["showResponses"],
        hideStaticBlocks,
        isDone,
      }) as DocViewContextType,
    [
      canAuthorPage,
      canInteractAsStudent,
      isAuthoringPage,
      readOnly,
      canWriteDoc,
      pageId,
      isProjecting,
      isFeaturing,
      isStaticContent,
      accessId,
      pageReleased,
      selectedPageTabVariant,
      showResponses,
      hideStaticBlocks,
      isDone,
    ],
  );

  return context;
};
